import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faBell,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import stopwatchIcon from "../../../assets/icons/stopwatch.svg";
import { differenceInDays, parseISO, format } from "date-fns";
import toast from "react-hot-toast";

import { getPatientGeneralDetails } from "../../../services/patient/details";
import { getPatientStatuses, getAllStatusOptions } from "../../../services/patient/settings";
import { getOutOfRangeReadings } from "../../../services/admin-case-manager/readings";
import Summary from "../summary";
import { formatDob } from "../../../utils/helper";
import Tooltip from "../../shared-modules/Tooltip";
import DeviceIcon from "./DeviceIcon";
import DeviceIconList from "./DeviceIconList";
import ComplianceHistoryModal from "./ComplianceHistoryModal";
import ContactModal from "./ContactModal";
import Modal from "../../shared-modules/Modal";
import Spinner from "../../spinner";
import styles from "./PatientCard.module.css";
import StatusesModal from "./StatusesModal";

// Alert configuration
const ALERT_CONFIG = {
  TYPES: {
    OUT_OF_RANGE: "out_of_range",
    REMINDER: "reminder",
    MONITORING_TIME: "monitoring_time",
    UNSEEN_READINGS: "unseen_readings",
  },
  SEVERITY: {
    CRITICAL: "critical",
    HIGH: "high",
    MEDIUM: "medium",
    LOW: "low",
  },
  THRESHOLDS: {
    MIN_RPM_SECONDS: 1200,
    READING_REMINDER_DAYS: 3,
  },
};

/**
 * PatientCard — redesigned patient summary block on the Details tab.
 *
 * Layout-pass scope (TH-A1): faithfully reproduces the Figma structure —
 * status pill row at top, 2-column field grid in the middle, full-width
 * 4-code time strip, Summary in a bordered region at the bottom.
 *
 * Status pills, the bell badge, the threshold icon, and the edit pencil
 * are visual placeholders for v1 (TH-A2 / TH-B2 / TH-B3 wire them up).
 * Field values bind to existing data via the same `getPatientGeneralDetails`
 * query the legacy <GeneralDetails /> uses, so this is a structural
 * replacement, not a data-flow change.
 *
 * Pixel reference: Figma "RPM TOCA AI Patient Dashboard" patient block.
 */
const PatientCard = ({ summary, summaryLoading }) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const [isStatusesModalOpen, setIsStatusesModalOpen] = useState(false);
  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isAlertsModalOpen, setIsAlertsModalOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [isAlertsLoading, setIsAlertsLoading] = useState(false);
  const [outOfRangeReadings, setOutOfRangeReadings] = useState([]);
  const [isOutOfRangeModalOpen, setIsOutOfRangeModalOpen] = useState(false);

  const { data: patient } = useQuery(
    ["patient-general-details", patientId],
    () => getPatientGeneralDetails(patientId),
    { enabled: !!patientId, staleTime: 5 * 60 * 1000 }
  );

  // Fetch patient statuses
  const { data: patientStatuses } = useQuery(
    ["patient-statuses", patientId],
    () => getPatientStatuses(),
    {
      enabled: !!patientId,
      staleTime: 2 * 60 * 1000,
    }
  );

  // Fetch all available status options for getting status names
  const { data: availableStatuses } = useQuery(
    ["global-statuses"],
    () => getAllStatusOptions(),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const user = patient?.user;
  const provider = patient?.provider;
  const caseManager = patient?.case_manager;
  const monitoringTime = patient?.monitoring_time_current_month;

  // Get status name by ID
  const getStatusName = (statusId) => {
    return availableStatuses?.find((s) => s.id === statusId)?.name || "";
  };

  // Helper functions for alerts
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Unknown date";
    try {
      const date = parseISO(dateString);
      return isNaN(date.getTime())
        ? "Unknown date"
        : format(date, "MMM d, yyyy 'at' h:mm a");
    } catch {
      return "Unknown date";
    }
  }, []);

  const calculateDaysSince = useCallback((dateString) => {
    if (!dateString) return null;
    try {
      const date = parseISO(dateString);
      if (isNaN(date.getTime())) return null;
      const daysDiff = differenceInDays(new Date(), date);
      return Math.max(0, daysDiff);
    } catch {
      return null;
    }
  }, []);

  // Alert creation functions
  const createAlert = useCallback(
    (type, severity, message, additionalData = {}) => {
      const alertTitles = {
        [ALERT_CONFIG.TYPES.OUT_OF_RANGE]: "Out Of Range",
        [ALERT_CONFIG.TYPES.REMINDER]: "Reading Reminder",
        [ALERT_CONFIG.TYPES.MONITORING_TIME]: "Monitoring Time Alert",
        [ALERT_CONFIG.TYPES.UNSEEN_READINGS]: "Unseen Readings",
      };

      return {
        id: `${type}-${Date.now()}`,
        type,
        severity,
        title: alertTitles[type] || "Alert",
        message,
        date: new Date().toISOString(),
        ...additionalData,
      };
    },
    []
  );

  const createOutOfRangeAlert = useCallback(
    (record) => {
      const daysSince = calculateDaysSince(record.created_at);
      let message;

      if (record.reading_type === "blood_pressure") {
        message = `Out of range Blood Pressure: ${record.systolic}/${record.diastolic} mm Hg`;
        if (record.heart_rate) message += ` and ${record.heart_rate} bpm`;
        message += ` recorded ${daysSince} days ago.`;
      } else {
        message = `Out of range ${record.reading_type_display || record.reading_type} detected on ${formatDate(record.created_at)}.`;
      }

      return createAlert(
        ALERT_CONFIG.TYPES.OUT_OF_RANGE,
        ALERT_CONFIG.SEVERITY.CRITICAL,
        message,
        {
          readingType: record.reading_type,
          date: record.created_at,
          formattedDate: formatDate(record.created_at),
        }
      );
    },
    [createAlert, calculateDaysSince, formatDate]
  );

  const createReadingReminderAlert = useCallback(
    (lastReadingDate, daysSince) => {
      const daysText = typeof daysSince === "number" ? daysSince : "many";
      const lastReadingText = lastReadingDate
        ? formatDate(lastReadingDate)
        : "No readings found";
      const message = `Patient hasn't submitted readings for ${daysText} days. Last reading: ${lastReadingText}.`;

      return createAlert(
        ALERT_CONFIG.TYPES.REMINDER,
        ALERT_CONFIG.SEVERITY.HIGH,
        message
      );
    },
    [createAlert, formatDate]
  );

  const createMonitoringTimeAlert = useCallback(
    (monitoringTime) => {
      const message = `Patient has only ${monitoringTime.formatted_rpm_time} of RPM monitoring time this month.`;
      return createAlert(
        ALERT_CONFIG.TYPES.MONITORING_TIME,
        ALERT_CONFIG.SEVERITY.MEDIUM,
        message
      );
    },
    [createAlert]
  );

  const createUnseenReadingsAlert = useCallback(() => {
    const message = "Patient has unseen readings that need to be reviewed.";
    return createAlert(
      ALERT_CONFIG.TYPES.UNSEEN_READINGS,
      ALERT_CONFIG.SEVERITY.LOW,
      message
    );
  }, [createAlert]);

  const checkReadingReminder = useCallback(
    (patientData) => {
      try {
        if (patientData.last_reading_record && patientData.last_reading_record.recorded_at) {
          const daysSinceLastReading = calculateDaysSince(patientData.last_reading_record.recorded_at);

          if (daysSinceLastReading !== null && daysSinceLastReading > 3) {
            return createReadingReminderAlert(
              patientData.last_reading_record.recorded_at,
              daysSinceLastReading
            );
          }
        } else {
          const daysSinceJoining = calculateDaysSince(patientData.created_at);

          if (daysSinceJoining !== null && daysSinceJoining > 3) {
            const message = `Patient joined ${daysSinceJoining} days ago but has not submitted any reading yet.`;
            return createAlert(
              ALERT_CONFIG.TYPES.REMINDER,
              ALERT_CONFIG.SEVERITY.HIGH,
              message
            );
          } else {
            const message = "Patient is new in the portal.";
            return createAlert(
              ALERT_CONFIG.TYPES.REMINDER,
              ALERT_CONFIG.SEVERITY.HIGH,
              message
            );
          }
        }

        return null;
      } catch (error) {
        return createAlert(
          ALERT_CONFIG.TYPES.REMINDER,
          ALERT_CONFIG.SEVERITY.MEDIUM,
          "Patient has no readings available."
        );
      }
    },
    [createReadingReminderAlert, calculateDaysSince, createAlert]
  );

  // Generate alerts from patient data
  const generateAlerts = useCallback(
    async (patientData) => {
      const alerts = [];

      if (patientData.last_out_of_range_record) {
        alerts.push(createOutOfRangeAlert(patientData.last_out_of_range_record));
      }

      if (patientData.unseen_readings === true) {
        alerts.push(createUnseenReadingsAlert());
      }

      if (
        patientData.monitoring_session === true &&
        patientData.monitoring_time?.total_rpm_seconds <
        ALERT_CONFIG.THRESHOLDS.MIN_RPM_SECONDS &&
        patientData.rpm
      ) {
        alerts.push(createMonitoringTimeAlert(patientData.monitoring_time));
      }

      const reminderAlert = checkReadingReminder(patientData);
      if (reminderAlert) {
        alerts.push(reminderAlert);
      }

      return alerts;
    },
    [
      createOutOfRangeAlert,
      createUnseenReadingsAlert,
      createMonitoringTimeAlert,
      checkReadingReminder,
    ]
  );

  // Fetch and generate alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      if (!patient) return;

      try {
        setIsAlertsLoading(true);
        const generatedAlerts = await generateAlerts(patient);
        setAlerts(generatedAlerts);
      } catch (error) {
        toast.error("Failed to fetch patient alerts");
        setAlerts([]);
      } finally {
        setIsAlertsLoading(false);
      }
    };

    fetchAlerts();
  }, [patient, generateAlerts]);

  // Calculate alert count
  const alertsCount = useMemo(() => alerts.length, [alerts.length]);

  // Handle resolve click for out of range alerts
  const handleResolveClick = useCallback(async () => {
    try {
      setIsAlertsLoading(true);
      const patientIdForAlert = patient?.id || sessionStorage.getItem("_pt_id");

      if (!patientIdForAlert) {
        toast.error("Patient ID not found");
        return;
      }

      const outOfRangeData = await getOutOfRangeReadings(patientIdForAlert);

      if (outOfRangeData?.readings?.length > 0) {
        setOutOfRangeReadings(outOfRangeData.readings);
        setIsAlertsModalOpen(false);
        setIsOutOfRangeModalOpen(true);
      } else {
        toast.error("No out-of-range readings found");
      }
    } catch (error) {
      toast.error("Failed to fetch out-of-range readings");
    } finally {
      setIsAlertsLoading(false);
    }
  }, [patient?.id]);

  // Handle review click for unseen readings
  const handleReviewClick = useCallback(() => {
    setIsAlertsModalOpen(false);
  }, []);

  // Handle remind patient click
  const handleRemindPatient = useCallback(() => {
    const phoneNumber = patient?.user?.primary_phone;
    if (phoneNumber) {
      toast.success(`Reminder sent to ${phoneNumber}`);
      setIsAlertsModalOpen(false);
    } else {
      toast.error("No phone number available for this patient");
    }
  }, [patient?.user?.primary_phone]);

  // Get alert actions based on alert type
  const getAlertActions = useCallback(
    (alert) => {
      if (alert.message === "Patient has no readings available.") {
        return null;
      }

      const actions = {
        [ALERT_CONFIG.TYPES.OUT_OF_RANGE]: (
          <button onClick={handleResolveClick} className={styles.resolveBtn}>
            Resolve
          </button>
        ),
        [ALERT_CONFIG.TYPES.UNSEEN_READINGS]: (
          <button onClick={handleReviewClick} className={styles.resolveBtn}>
            Review
          </button>
        ),
        [ALERT_CONFIG.TYPES.REMINDER]: (
          <button onClick={handleRemindPatient} className={styles.resolveBtn}>
            Remind Patient
          </button>
        ),
        [ALERT_CONFIG.TYPES.MONITORING_TIME]: (
          <button
            onClick={() => {
              setIsAlertsModalOpen(false);
              toast("Please schedule a monitoring call");
            }}
            className={styles.acknowledgeBtn}
          >
            Schedule
          </button>
        ),
      };

      return actions[alert.type] || null;
    },
    [handleResolveClick, handleReviewClick, handleRemindPatient]
  );

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardMainLayout}>
          {/* Left column: Status row and field grid */}
          <div className={styles.cardLeftColumn}>
            {/* Status pill row with real patient statuses */}
            <div className={styles.statusRow}>
              <div className={styles.statusPills}>
                {patientStatuses && patientStatuses.length > 0 ? (
                  patientStatuses.map((patientStatus) => {
                    const statusName = getStatusName(patientStatus.status);
                    return (
                      <span key={patientStatus.id} className={styles.statusPill}>
                        {statusName || "Unknown"}
                        <Tooltip content={patientStatus.description || "No description"} position="top">
                          <span className={styles.statusInfoDot}>i</span>
                        </Tooltip>
                      </span>
                    );
                  })
                ) : (
                  <span className={styles.noStatuses}>No statuses</span>
                )}
                <button
                  type="button"
                  className={styles.statusEditBtn}
                  onClick={() => setIsStatusesModalOpen(true)}
                  aria-label="Edit statuses"
                  title="Edit statuses"
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </button>
              </div>
            </div>

            {/* 2-column patient field grid */}
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <Field
                  label="Patient Name"
                  value={
                    <div className={styles.nameWithDevices}>
                      <span className={styles.nameText}>
                        {user?.first_name && user?.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : "—"}
                      </span>

                      <DeviceIconList
                        devices={patient?.devices}
                        size="default"
                      />
                    </div>
                  }
                />
                <Field label="Date of Birth" value={formatDob(user?.dob) || "—"} />
                <Field label="Gender" value={user?.gender || "—"} />
                <Field
                  label="Primary Phone"
                  value={user?.primary_phone || "—"}
                >
                  <button
                    type="button"
                    className={styles.showmore}
                    onClick={() => setIsContactModalOpen(true)}
                  >
                    Show more
                  </button>
                </Field>
                <Field label="Email" value={user?.email || "—"} />
              </div>
              <div className={styles.fieldGroup}>
                <Field
                  label="Provider"
                  value={
                    provider?.first_name
                      ? `${provider.first_name} ${provider.last_name || ""}`.trim()
                      : "—"
                  }
                />
                <Field
                  label="Case Manager"
                  value={
                    caseManager?.first_name
                      ? `${caseManager.first_name} ${caseManager.last_name || ""}`.trim()
                      : "—"
                  }
                />
                <Field
                  label="Days of readings"
                  value={
                    patient?.current_month_reading_days != null
                      ? String(patient.current_month_reading_days)
                      : "—"
                  }
                />
                <Field
                  label="Preferred Language"
                  value={patient?.preferred_language || "—"}
                />
              </div>
            </div>
          </div>

          {/* Right column: Action buttons */}
          <div className={styles.cardRightColumn}>
            <button
              type="button"
              className={styles.notificationBtn}
              onClick={() => setIsAlertsModalOpen(true)}
              aria-label="Notifications"
              title="Notifications"
            >
              <img src="/assets/images/svgs/Group.svg" alt="notifications" />
              {alertsCount > 0 && (
                <span className={styles.notificationBadge}>{alertsCount}</span>
              )}
            </button>
            <button
              type="button"
              className={styles.complianceBtn}
              onClick={() => setIsComplianceModalOpen(true)}
              aria-label="Compliance history"
              title="Compliance history"
            >
              <img src="/assets/images/svgs/compliance.svg" alt="Compliance" />
            </button>

          </div>
        </div>

        {/* 4-code time strip — RPM and CCM real, RTM and PCM placeholder */}
        <div className={styles.timeStrip}>
          <img src={stopwatchIcon} alt="" aria-hidden="true" className={styles.timeStripIcon} />
          <TimeCode label="RPM Time" value={monitoringTime?.formatted_rpm_time} />
          <TimeCode label="CCM Time" value={monitoringTime?.formatted_ccm_time} />
          <TimeCode label="RTM Time" value="—" />
          <TimeCode label="PCM Time" value="—" />
        </div>

        {/* Summary block */}
        <div className={styles.summaryWrap}>
          <h4 className={styles.summaryHeading}>Summary</h4>
          {/* <div className={styles.summaryBody}> */}
          <Summary loading={summaryLoading} summary={summary} />
          {/* </div> */}
        </div>
      </div>

      {/* Statuses Modal */}
      <StatusesModal
        isOpen={isStatusesModalOpen}
        onClose={() => setIsStatusesModalOpen(false)}
      />

      {/* Compliance History Modal */}
      <ComplianceHistoryModal
        isOpen={isComplianceModalOpen}
        onClose={() => setIsComplianceModalOpen(false)}
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        user={user}
      />

      {/* Alerts Modal */}
      <Modal
        title="Patient Alerts"
        isOpen={isAlertsModalOpen}
        onClose={() => setIsAlertsModalOpen(false)}
        width="800px"
      >
        <div className={styles.alertsContainer}>
          {isAlertsLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner />
              <p className={styles.loadingText}>Loading patient alerts...</p>
            </div>
          ) : alerts.length === 0 ? (
            <div className={styles.noAlertsMessage}>
              <p>No alerts found for this patient.</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                className={`${styles.alertItem} ${styles[`alertSeverity-${alert.severity}`]}`}
              >
                <div className={styles.alertHeader}>
                  <span className={styles.alertHeaderText}>{alert.title}</span>
                </div>

                <div className={styles.alertContent}>
                  <div
                    className={`${styles.statusIndicator} ${styles[`${alert.severity}Indicator`]}`}
                  >
                    <div className={styles.outerCircle}>
                      <div className={styles.innerCircle}></div>
                    </div>
                  </div>
                  <div className={styles.alertMessage}>
                    <div className={styles.messageText}>{alert.message}</div>
                    <div className={styles.timestamp}>
                      {alert.formattedDate}
                    </div>
                  </div>
                  {getAlertActions(alert)}
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>
    </>
  );
};

const Field = ({ label, value, icon, onIconClick, iconLabel, devices, connectivityIcon, deviceIconList, children }) => (
  <div className={`${styles.field} ${deviceIconList ? styles.fieldWithIcons : ''}`}>
    <span className={styles.fieldLabel}>{label} :</span>
    <div className={styles.fieldContent}>
      <span className={styles.fieldValue}>
        {value}
        {connectivityIcon && <span style={{ marginLeft: "8px" }}>{connectivityIcon}</span>}
        {icon && (
          <img
            src={icon}
            alt={iconLabel || "Icon"}
            className={styles.fieldIcon}
            onClick={onIconClick}
            title={iconLabel}
          />
        )}
        {devices && devices.map((device, index) => (
          <img
            key={index}
            src={device.icon}
            alt={device.label}
            className={styles.fieldIcon}
            title={device.label}
          />
        ))}
        {children}
      </span>
      {deviceIconList && <div className={styles.deviceIconRow}>{deviceIconList}</div>}
    </div>
  </div>
);

const TimeCode = ({ label, value }) => (
  <span className={styles.timeCode}>
    <span className={styles.timeCodeLabel}>{label} :</span>
    <strong className={styles.timeCodeValue}>{value || "—"}</strong>
  </span>
);

export default PatientCard;
